import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import AsyncTable, { Column, RequestPage } from '../../../../components/AsyncTable';
import Btn from '../../../../components/Buttons/Btn';
import Modal from '../../../../components/Modal';
import { frontendNotification } from '../../../../components/Notification';
import NavMenu from '../../../../components/NavMenu';
import { NavMenyItem } from '../../../../components/NavMenu/types';
import SelectAsyncJSX from '../../../../components/SelectAsync';
import SingleSelect from '../../../../components/SingleSelect';
import { ActionButtonsContainer } from '../../../../components/TablePaginator/styles';
import { PreviewIcon } from '../../../../icons';
import { superContactsReq, tenantsReq } from '../../../../services/requests';
import { Contact } from '../../../../types/apiResponse/contact';
import ContentContainer from '../../../../templates/Content/ContentContainer';
import {
  EmailLog,
  EmailLogSendMode,
  EmailSend,
  EmailSmtpConfig,
} from '../../../../types/apiResponse/email';
import {
  SelectOption,
  getBooleanOptions,
  getSelectOptionByBoolean,
} from '../../../../utils/getSelectOptions';

type TabValue = 'logs' | 'smtpConfig' | 'sendEmail';
type RecipientMode = 'all' | 'tenant' | 'contact';

type RecipientSelection = {
  mode: RecipientMode;
  selectedTenant: SelectOption | null;
  selectedContacts: SelectOption[];
  customEmailsInput: string;
};

type TenantOptionItem = {
  id: number;
  name: string;
};

const FrontOnlyNotice = ({ message }: { message: string }) => (
  <div
    style={{
      marginBottom: '1rem',
      padding: '0.75rem 1rem',
      borderRadius: '6px',
      border: '1px solid rgba(0, 0, 0, 0.1)',
      background: 'rgba(0, 0, 0, 0.03)',
    }}
  >
    {message}
  </div>
);

const RecipientSelectorModal = ({
  showModal,
  closeModal,
  value,
  onSave,
}: {
  showModal: boolean;
  closeModal: () => void;
  value: RecipientSelection;
  onSave: (value: RecipientSelection) => void;
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<RecipientMode>(value.mode);
  const [selectedTenant, setSelectedTenant] = useState<SelectOption | null>(value.selectedTenant);
  const [selectedContacts, setSelectedContacts] = useState<SelectOption[]>(value.selectedContacts);
  const [customEmailsInput, setCustomEmailsInput] = useState<string>(value.customEmailsInput);

  useEffect(() => {
    if (!showModal) {
      return;
    }

    setMode(value.mode);
    setSelectedTenant(value.selectedTenant);
    setSelectedContacts(value.selectedContacts);
    setCustomEmailsInput(value.customEmailsInput);
  }, [showModal, value]);

  const modeOptions: SelectOption[] = [
    { value: 'all', label: t('sendToAll') },
    { value: 'tenant', label: t('sendToSpecificTenant') },
    { value: 'contact', label: t('sendToSpecificContact') },
  ];

  const handleSave = () => {
    if (mode === 'tenant' && !selectedTenant) {
      frontendNotification({
        message: t('selectATenant'),
        type: 'warning',
      });

      return;
    }

    if (mode === 'contact' && selectedContacts.length === 0 && !customEmailsInput.trim()) {
      frontendNotification({
        message: t('selectAtLeastOneContact'),
        type: 'warning',
      });

      return;
    }

    onSave({ mode, selectedTenant, selectedContacts, customEmailsInput });
    closeModal();
  };

  return (
    <Modal
      title={t('recipientSelection')}
      showModal={showModal}
      closeModal={closeModal}
      cancelButton={closeModal}
      action={handleSave}
      actionNameBtn={t('select')}
      bodyStyle={{ overflow: 'visible' }}
    >
      <div className="row">
        <div className="inputWithLabel" style={{ minWidth: '280px' }}>
          <div className="label">{t('sendMode') as string}</div>
          <SingleSelect
            value={modeOptions.find((o) => o.value === mode) ?? null}
            options={modeOptions}
            onChange={(option) => {
              setMode(option.value as RecipientMode);
              setSelectedTenant(null);
              setSelectedContacts([]);
              setCustomEmailsInput('');
            }}
            placeholder={t('select')}
          />
        </div>
      </div>

      {mode === 'tenant' && (
        <div className="row">
          <div className="inputWithLabel" style={{ minWidth: '280px' }}>
            <div className="label">{t('tenant') as string}</div>
            <SelectAsyncJSX
              value={selectedTenant}
              onChange={setSelectedTenant}
              request={tenantsReq}
              reqResponseToOption={{
                mapper: {
                  label: (item: TenantOptionItem) => `${item.id} - ${item.name}`,
                  value: (item: TenantOptionItem) => item.id,
                },
              }}
              placeholder={t('select')}
              query="quantityPerPage=50"
            />
          </div>
        </div>
      )}

      {mode === 'contact' && (
        <>
          <div className="row">
            <div className="inputWithLabel" style={{ minWidth: '320px' }}>
              <div className="label">{t('contacts') as string}</div>
              <SelectAsyncJSX
                value={selectedContacts}
                onChange={setSelectedContacts}
                request={superContactsReq}
                isMulti
                reqResponseToOption={{
                  mapper: {
                    label: (item: Contact) => {
                      const tenantLabel = item.tenant
                        ? ` (${item.tenant.name})`
                        : ` (${item.tenant_id})`;
                      return `${item.contact}${tenantLabel}${item.email ? ` — ${item.email}` : ''}`;
                    },
                    value: (item: Contact) => item.id,
                  },
                }}
                placeholder={t('select')}
                query="quantityPerPage=50"
              />
            </div>
          </div>

          <div className="row">
            <div className="itemFormWithLabel" style={{ minWidth: '320px' }}>
              <label htmlFor="customEmailsInput">{t('orTypeEmails') as string}</label>
              <input
                id="customEmailsInput"
                type="text"
                value={customEmailsInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setCustomEmailsInput(e.target.value)
                }
                placeholder="email1@ex.com, email2@ex.com"
              />
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

const EmailContentModal = ({
  showModal,
  closeModal,
  log,
}: {
  showModal: boolean;
  closeModal: () => void;
  log: EmailLog | null;
}) => {
  const { t } = useTranslation();

  if (!log) return null;

  return (
    <Modal
      title={log.subject ?? t('emailLogs')}
      showModal={showModal}
      closeModal={closeModal}
      onlyCloseButton
      style={{ width: '70vw', maxWidth: '70vw', height: '75vh', maxHeight: '75vh' }}
      bodyStyle={{ overflow: 'auto', flex: 1, width: '100%', boxSizing: 'border-box' }}
    >
      <div style={{ marginBottom: '0.75rem', fontSize: '0.85rem', opacity: 0.65 }}>
        {t('createdAt')}: {log.created_at ?? '-'}
      </div>

      {log.body ? (
        log.isHtml ? (
          // eslint-disable-next-line react/no-danger
          <div dangerouslySetInnerHTML={{ __html: log.body }} />
        ) : (
          <pre
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              margin: 0,
              fontSize: '0.95rem',
              lineHeight: '1.6',
            }}
          >
            {log.body}
          </pre>
        )
      ) : (
        <span>-</span>
      )}
    </Modal>
  );
};

const MOCKED_LOGS: EmailLog[] = [
  {
    id: 1,
    NumeroPedido: null,
    motivoErro: null,
    logErro: null,
    dataHoraCadastro: '2026-05-04 10:00:00',
    emailDestinatarios: null,
    send_mode: 'all',
    send_mode_reference: null,
    subject: 'Novidade SmartSell — Maio 2026',
    body: 'Olá!\n\nMensagem de teste para todos os contatos.\n\nAtenciosamente,\nEquipe SmartSell',
    isHtml: false,
    connection_info: null,
    created_at: '2026-05-04 10:00:00',
    updated_at: '2026-05-04 10:00:00',
  },
];

const DispatchLogs = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [logs, setLogs] = useState<EmailLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);

  const getSendModeLabel = (mode: EmailLogSendMode | null, reference: string | null): string => {
    if (mode === 'all') return t('sendModeAll');
    if (mode === 'tenant') return `${t('sendModeTenant')}${reference ? `: ${reference}` : ''}`;
    if (mode === 'contact') return `${t('sendModeContact')}${reference ? `: ${reference}` : ''}`;
    return '-';
  };

  const columns: Column<EmailLog>[] = [
    {
      label: t('id') as string,
      accessor: 'id',
    },
    {
      label: t('subject') as string,
      accessor: 'subject',
    },
    {
      label: t('sendMode') as string,
      Cell: (row: EmailLog) => (
        <span>{getSendModeLabel(row.send_mode, row.send_mode_reference)}</span>
      ),
    },
    {
      label: t('createdAt') as string,
      accessor: 'created_at',
    },
    {
      label: '',
      Cell: (row: EmailLog) => (
        <ActionButtonsContainer>
          <PreviewIcon
            title={t('toView')}
            onClick={() => setSelectedLog(row)}
          />
        </ActionButtonsContainer>
      ),
      headerCellStyle: { textAlign: 'center' },
      cellStyle: { textAlign: 'center' },
    },
  ];

  const requestPage: RequestPage = async () => ({
    data: MOCKED_LOGS,
    hasMore: false,
    totalPage: 1,
  });

  return (
    <>
      <FrontOnlyNotice message={t('superadminDispatchFrontendOnly')} />

      <EmailContentModal
        showModal={Boolean(selectedLog)}
        closeModal={() => setSelectedLog(null)}
        log={selectedLog}
      />

      <AsyncTable
        tableName={t('emailLogs')}
        columns={columns}
        value={logs}
        onChange={setLogs}
        requestPage={requestPage}
        reqListener={[]}
        options={{
          styles: {
            primaryColor: `${theme.colors.surface}`,
            secondaryColor: `${theme.colors.onSurface}`,
            alternateRowColor: theme.colors.onSurfaceLight,
            textColor: theme.colors.text,
          },
          quantityPerPageLabel: t('quantityPerPage'),
          searchPlaceholder: t('search'),
          totalPageLabel: `${t('totalPages')}: `,
        }}
      />
    </>
  );
};

const DispatchSmtpConfig = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [smtpConfigData, setSmtpConfigData] = useState<EmailSmtpConfig>({
    host: '',
    username: '',
    password: '',
    port: '587',
    smtpSecure: 'tls',
    smtpAutoTLS: true,
  });

  const handleSaveBtn = () => {
    setIsLoading(true);

    frontendNotification({
      message: t('superadminDispatchFrontendOnly'),
      type: 'warning',
    });

    setIsLoading(false);
  };

  return (
    <>
      <FrontOnlyNotice message={t('superadminDispatchFrontendOnly')} />

      <div className="row">
        <div className="itemFormWithLabel">
          <label htmlFor="host">{t('host') as string}</label>
          <input
            type="text"
            value={smtpConfigData.host as string}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setSmtpConfigData({ ...smtpConfigData, host: event.target.value })
            }
            disabled={isLoading}
          />
        </div>

        <div className="itemFormWithLabel" style={{ maxWidth: '10rem' }}>
          <label htmlFor="port">{t('port') as string}</label>
          <input
            type="number"
            min={1}
            value={smtpConfigData.port as string}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setSmtpConfigData({ ...smtpConfigData, port: event.target.value })
            }
            disabled={isLoading}
          />
        </div>

        <div className="itemFormWithLabel" style={{ maxWidth: '10rem' }}>
          <label htmlFor="smtpSecure">{t('smtpSecure') as string}</label>
          <input
            type="text"
            value={smtpConfigData.smtpSecure as string}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setSmtpConfigData({ ...smtpConfigData, smtpSecure: event.target.value })
            }
            disabled={isLoading}
          />
        </div>

        <div className="itemFormWithLabel" style={{ maxWidth: '10rem' }}>
          <label htmlFor="smtpAutoTLS">{t('smtpAutoTLS') as string}</label>
          <SingleSelect
            value={getSelectOptionByBoolean(smtpConfigData.smtpAutoTLS)}
            options={getBooleanOptions()}
            onChange={(option) =>
              setSmtpConfigData({ ...smtpConfigData, smtpAutoTLS: option.value })
            }
            placeholder={t('yesOrNo')}
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="row">
        <div className="itemFormWithLabel">
          <label htmlFor="username">{t('username') as string}</label>
          <input
            type="text"
            value={smtpConfigData.username as string}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setSmtpConfigData({ ...smtpConfigData, username: event.target.value })
            }
            disabled={isLoading}
          />
        </div>

        <div className="itemFormWithLabel">
          <label htmlFor="password">{t('password') as string}</label>
          <input
            type="text"
            value={smtpConfigData.password as string}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setSmtpConfigData({ ...smtpConfigData, password: event.target.value })
            }
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="row">
        <div className="itemForm webkitRight" style={{ marginRight: '0.5rem' }}>
          <Btn text={t('save')} onClick={handleSaveBtn} disabled={isLoading} />
        </div>
      </div>
    </>
  );
};

const DispatchSendEmail = () => {
  const { t } = useTranslation();
  const [showRecipientModal, setShowRecipientModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [recipientSelection, setRecipientSelection] = useState<RecipientSelection>({
    mode: 'all',
    selectedTenant: null,
    selectedContacts: [],
    customEmailsInput: '',
  });
  const [data, setData] = useState<EmailSend>({
    to: [],
    subject: '',
    body: '',
    isHtml: false,
    altBody: '',
    queued: true,
  });

  const getRecipientSummary = () => {
    if (recipientSelection.mode === 'all') {
      return t('allRecipients');
    }

    if (recipientSelection.mode === 'tenant') {
      return recipientSelection.selectedTenant?.label || t('selectATenant');
    }

    const parts: string[] = [];
    if (recipientSelection.selectedContacts.length > 0) {
      parts.push(`${recipientSelection.selectedContacts.length} ${t('contacts')}`);
    }
    if (recipientSelection.customEmailsInput.trim()) {
      parts.push(recipientSelection.customEmailsInput.trim());
    }
    return parts.length > 0 ? parts.join(', ') : t('selectAtLeastOneContact');
  };

  const handleSend = () => {
    if (recipientSelection.mode === 'tenant' && !recipientSelection.selectedTenant) {
      frontendNotification({
        message: t('selectATenant'),
        type: 'warning',
      });

      return;
    }

    if (
      recipientSelection.mode === 'contact' &&
      recipientSelection.selectedContacts.length === 0 &&
      !recipientSelection.customEmailsInput.trim()
    ) {
      frontendNotification({
        message: t('selectAtLeastOneContact'),
        type: 'warning',
      });

      return;
    }

    setIsLoading(true);

    frontendNotification({
      message: t('superadminDispatchFrontendOnly'),
      type: 'warning',
    });

    setIsLoading(false);
  };

  return (
    <>
      <FrontOnlyNotice message={t('superadminDispatchFrontendOnly')} />

      <div className="row">
        <div className="itemFormWithLabel">
          <label htmlFor="to">{t('to') as string}</label>
          <Btn
            text={t('selectRecipients')}
            onClick={() => setShowRecipientModal(true)}
            disabled={isLoading}
            style={{ maxWidth: '220px', marginLeft: '0' }}
          />
          <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.8 }}>
            {t('selectedRecipientTarget') as string}: {getRecipientSummary()}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="itemFormWithLabel">
          <label htmlFor="subject">{t('subject') as string}</label>
          <input
            type="text"
            value={data.subject as string}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setData({ ...data, subject: event.target.value })
            }
            disabled={isLoading}
          />
        </div>

        <div className="itemFormWithLabel" style={{ maxWidth: '15rem' }}>
          <label htmlFor="isHtml">{t('isTheContentHtml') as string}</label>
          <SingleSelect
            value={getSelectOptionByBoolean(data.isHtml)}
            options={getBooleanOptions()}
            onChange={(option) => setData({ ...data, isHtml: option.value })}
            placeholder={t('yesOrNo')}
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="row">
        <div className="itemFormWithLabel">
          <label htmlFor="content">{t('content') as string}</label>
          <textarea
            className="input"
            value={data.body as string}
            onChange={(event) => setData({ ...data, body: event.target.value })}
            disabled={isLoading}
            rows={10}
          />
        </div>
      </div>

      <div className="row">
        <div className="itemForm webkitRight" style={{ marginRight: '0.5rem' }}>
          <Btn text={t('send')} onClick={handleSend} disabled={isLoading} />
        </div>
      </div>

      <RecipientSelectorModal
        showModal={showRecipientModal}
        closeModal={() => setShowRecipientModal(false)}
        value={recipientSelection}
        onSave={setRecipientSelection}
      />
    </>
  );
};

const ContactDispatchPage = () => {
  const { t } = useTranslation();
  const [tabSelected, setTabSelected] = useState<TabValue>('logs');

  const navMenuItems: NavMenyItem[] = [
    {
      title: t('logs'),
      action: () => setTabSelected('logs'),
    },
    {
      title: t('smtpConfig'),
      action: () => setTabSelected('smtpConfig'),
    },
    {
      title: t('sendEmail'),
      action: () => setTabSelected('sendEmail'),
    },
  ];

  const renderTab = () => {
    switch (tabSelected) {
      case 'smtpConfig':
        return <DispatchSmtpConfig />;
      case 'sendEmail':
        return <DispatchSendEmail />;
      case 'logs':
      default:
        return <DispatchLogs />;
    }
  };

  return (
    <ContentContainer title={t('dispatch')}>
      <NavMenu items={navMenuItems} />
      {renderTab()}
    </ContentContainer>
  );
};

export default ContactDispatchPage;
